import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import images from "../../../../utilities/images/images";
import {
  fetchAdaptiveHomepageRequest,
  fetchAdaptiveAllQuestionListRequest,
  postAdaptiveLearningSummaryRequest,
} from "../../../../redux/adaptive_qbank/adaptiveQBankSlice";
import structureIcon from "../../../../utilities/images/adaptive_qbank/icon.png";
import editIcon from "../../../../utilities/images/adaptive_qbank/edit_icon.png";
import "./AdaptiveNextFocusSet.css";
import { postAdaptiveSubTopicListRequest } from "../../../../redux/adaptive_qbank/adaptiveQBankSlice";
import CustomizeSubtopicModal from "../CustomizeSubtopicModal";
import AdaptiveUpgradeModal from "../components/AdaptiveUpgradeModal";



const getAdaptiveCards = (homepagePayload) => {
  if (!homepagePayload) return [];
  const root = homepagePayload?.data ?? homepagePayload;
  if (!root) return [];
  if (Array.isArray(root)) return root;
  if (Array.isArray(root?.data)) return root.data;
  if (Array.isArray(root?.cards)) return root.cards;
  if (Array.isArray(root?.list)) return root.list;
  return [];
};

const getHomepageRoot = (payload) => payload?.data ?? payload ?? {};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.list)) return value.list;
  if (Array.isArray(value?.result)) return value.result;
  return [];
};

const pickValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

/** Parses API values like "FS-6", 6, "6" into a positive focus-set order number. */
const parseFocusSetOrderFromApi = (value) => {
  if (value === undefined || value === null || value === "") return null;
  if (typeof value === "number" && Number.isFinite(value) && value > 0) return value;
  const str = String(value).trim();
  const match = str.match(/(\d+)\s*$/);
  if (match) {
    const n = Number(match[1]);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const digits = str.replace(/\D/g, "");
  if (!digits) return null;
  const n = Number(digits);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const normalizeCycleNumber = (item) => {
  const cycleRaw = String(
    pickValue(
      item?.learning_cycle,
      item?.learningCycle,
      item?.cycle,
      item?.cycle_no,
      item?.cycleNo
    ) || ""
  );
  const num = Number(cycleRaw.replace(/[^\d]/g, ""));
  return Number.isFinite(num) && num > 0 ? num : 1;
};

const isCompletedFocusSet = (item) => {
  const direct = pickValue(item?.is_completed, item?.completed, item?.isCompleted);
  if (direct === true || Number(direct) === 1) return true;
  const status = String(pickValue(item?.status, item?.state, item?.result, "")).toLowerCase();
  return status.includes("completed") || status.includes("mastered") || status.includes("done");
};

const isActiveFocusSet = (item) => {
  const direct = pickValue(item?.is_current, item?.current, item?.isActive);
  if (direct === true || Number(direct) === 1) return true;
  const status = String(pickValue(item?.status, item?.state, "")).toLowerCase();
  return status.includes("current") || status.includes("active");
};

const normalizeFocusSet = (item, idx) => {
  const order = Number(
    pickValue(item?.focus_set_no, item?.focusSetNo, item?.order, item?.id, idx + 1)
  );
  return {
    label: `FS-${order}`,
    order,
    cycle: normalizeCycleNumber(item),
    completed: isCompletedFocusSet(item),
    active: isActiveFocusSet(item),
  };
};

const flattenFocusSetsFromLearningCycles = (root) => {
  const cycles = toArray(pickValue(root?.learning_cycles, root?.learningCycles));
  if (!cycles.length) return [];
  const merged = [];
  for (const cycleEl of cycles) {
    const cycleNum = normalizeCycleNumber(cycleEl);
    const sets = toArray(
      pickValue(
        cycleEl?.focus_sets,
        cycleEl?.focusSets,
        cycleEl?.sets,
        cycleEl?.focus_set_list,
        cycleEl?.focusSetList
      )
    );
    for (const s of sets) {
      merged.push({
        ...s,
        learning_cycle: pickValue(s?.learning_cycle, s?.learningCycle, cycleEl?.learning_cycle, cycleNum),
        cycle: pickValue(s?.cycle, cycleEl?.cycle, cycleNum),
      });
    }
  }
  return merged;
};

const getFocusSets = (homepagePayload) => {
  const root = getHomepageRoot(homepagePayload);
  let rawSets = toArray(
    pickValue(
      root?.focus_sets,
      root?.focusSets,
      root?.learning_cycle_sets,
      root?.learningCycleSets,
      root?.progress,
      root?.progress_list,
      root?.focus_set_progress
    )
  );
  if (rawSets.length === 0) {
    rawSets = flattenFocusSetsFromLearningCycles(root);
  }

  const normalizedSets = rawSets.map(normalizeFocusSet).sort((a, b) => a.order - b.order);
  if (normalizedSets.length > 0 && !normalizedSets.some((set) => set.active)) {
    const currentOrder = parseFocusSetOrderFromApi(
      pickValue(
        root?.current_focus_set,
        root?.currentFocusSet,
        root?.current_focus_set_no,
        root?.currentFocusSetNo,
        root?.active_focus_set_no,
        root?.activeFocusSetNo,
        root?.focus_set_no,
        root?.focusSetNo
      )
    );
    if (currentOrder !== null) {
      return normalizedSets.map((set) => ({
        ...set,
        active: set.order === currentOrder,
        completed: set.order < currentOrder ? true : set.completed,
      }));
    }
    const firstIncompleteIndex = normalizedSets.findIndex((set) => !set.completed);
    const fallbackActiveIndex =
      firstIncompleteIndex >= 0 ? firstIncompleteIndex : normalizedSets.length - 1;
    return normalizedSets.map((set, index) => ({
      ...set,
      active: index === fallbackActiveIndex,
    }));
  }
  return normalizedSets;
};

const getFallbackProgressMeta = (root) => {
  const cycleOneCompleted = Number(
    pickValue(
      root?.cycle_1_completed_focus_sets,
      root?.cycle1_completed_focus_sets,
      root?.cycle_1_completed_count,
      root?.cycle1_completed_count,
      root?.cycle_1_completed,
      root?.cycle1_completed,
      root?.learning_cycle_1_completed,
      root?.learningCycle1Completed,
      0
    )
  );
  const cycleTwoCompleted = Number(
    pickValue(
      root?.cycle_2_completed_focus_sets,
      root?.cycle2_completed_focus_sets,
      root?.cycle_2_completed_count,
      root?.cycle2_completed_count,
      root?.cycle_2_completed,
      root?.cycle2_completed,
      root?.learning_cycle_2_completed,
      root?.learningCycle2Completed,
      0
    )
  );
  const currentFocusSetNumeric = Number(
    pickValue(
      root?.current_focus_set_no,
      root?.currentFocusSetNo,
      root?.active_focus_set_no,
      root?.activeFocusSetNo,
      root?.focus_set_no,
      root?.focusSetNo
    )
  );
  const currentFocusSetParsed =
    Number.isFinite(currentFocusSetNumeric) && currentFocusSetNumeric > 0
      ? currentFocusSetNumeric
      : parseFocusSetOrderFromApi(
          pickValue(
            root?.current_focus_set,
            root?.currentFocusSet,
            root?.current_focus_set_label,
            root?.currentFocusSetLabel
          )
        );

  return {
    completedByCycle: {
      1: Number.isFinite(cycleOneCompleted) ? cycleOneCompleted : 0,
      2: Number.isFinite(cycleTwoCompleted) ? cycleTwoCompleted : 0,
    },
    currentFocusSet: currentFocusSetParsed,
  };
};

const buildFallbackFocusSets = (
  perCycle = 5,
  totalCycles = 2,
  completedByCycle = {},
  currentFocusSet = null
) => {
  const safePerCycle = Number.isFinite(perCycle) && perCycle > 0 ? perCycle : 5;
  const safeCycles = Number.isFinite(totalCycles) && totalCycles > 0 ? totalCycles : 2;
  const result = [];
  let order = 1;

  for (let cycle = 1; cycle <= safeCycles; cycle += 1) {
    const cycleCompleted = Number(completedByCycle?.[cycle] ?? 0);
    for (let i = 0; i < safePerCycle; i += 1) {
      const isCompleted =
        currentFocusSet !== null && Number.isFinite(currentFocusSet)
          ? order < currentFocusSet
          : i < cycleCompleted;
      result.push({
        label: `FS-${order}`,
        order,
        cycle,
        completed: isCompleted,
        active:
          currentFocusSet !== null && Number.isFinite(currentFocusSet)
            ? order === currentFocusSet
            : order === 1,
      });
      order += 1;
    }
  }

  return result;
};

const getDifficultyValue = (subtopic) => {
  const rawDifficulty = String(
    pickValue(
      subtopic?.current_difficulty,
      subtopic?.selecteddifficulty,
      subtopic?.difficulty,
      subtopic?.level,
      subtopic?.mastery_tag,
      subtopic?.subtopic_level,
      "Easy"
    ) || "Easy"
  ).toLowerCase();

  if (rawDifficulty.includes("hard")) return "Hard";
  if (rawDifficulty.includes("medium")) return "Medium";
  return "Easy";
};

const FOCUS_SETS_PER_LEARNING_CYCLE = 5;

const toRoman = (num) => {
  const romanMap = [
    [1000, "M"],
    [900, "CM"],
    [500, "D"],
    [400, "CD"],
    [100, "C"],
    [90, "XC"],
    [50, "L"],
    [40, "XL"],
    [10, "X"],
    [9, "IX"],
    [5, "V"],
    [4, "IV"],
    [1, "I"],
  ];
  let value = Number(num);
  if (!Number.isFinite(value) || value <= 0) return String(num);
  let result = "";
  for (const [base, symbol] of romanMap) {
    while (value >= base) {
      result += symbol;
      value -= base;
    }
  }
  return result;
};

const getLocalDateKey = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const AdaptiveNextFocusSet = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [isCustomizeEnabled, setIsCustomizeEnabled] = useState(false);
  const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [selectedCustomizeTopic, setSelectedCustomizeTopic] = useState(null);

  const topic = location.state?.topic;
  const pausedFocusSetNoFromRoute = Number(location?.state?.pausedFocusSetNo || 0);
  const isPausedJourneyFromRoute = location?.state?.isPaused === true;
  const suppressResumeFromRoute = location?.state?.suppressResume === true;
  const openUpgradeModalFromRoute = location?.state?.openUpgradeModal === true;
  const completedFocusSetNoFromRoute = Number(location?.state?.completedFocusSetNo || 0);
  const nextFocusSetNoFromRoute = Number(location?.state?.focusSetNo || 0);

  const topicId = topic?.topic_id;
  const subjectName = topic?.subject || topic?.sub_name || topic?.subject_name || "";
  const topicName = topic?.topic_name;

  const { adaptiveHomepage, loading, error } = useSelector(
    (state) => state.adaptiveQBank
  );
  const { subTopicList } = useSelector((state) => state.adaptiveQBank);

  const selectedDifficultyLevel = useMemo(() => {
    const difficultyLevels =
      subTopicList?.data?.difficultyLevels || subTopicList?.difficultyLevels || [];
    const selectedLevel = Array.isArray(difficultyLevels)
      ? difficultyLevels.find((item) => item?.is_selected === 1 || item?.is_selected === true)
      : null;
    return selectedLevel?.level || "";
  }, [subTopicList]);

  const subtopics = useMemo(
    () =>
      toArray(
        pickValue(
          subTopicList?.data?.subtopic_list,
          subTopicList?.data?.subtopics,
          subTopicList?.data?.sub_topic,
          subTopicList?.data?.topic_subtopics,
          subTopicList?.data?.list,
          subTopicList?.data,
          subTopicList?.subtopic_list,
          subTopicList?.subtopics,
          subTopicList?.sub_topic,
          subTopicList?.topic_subtopics,
          subTopicList?.list
        )
      ),
    [subTopicList]
  );

  useEffect(() => {
    if (!isCustomizeEnabled || !topicId || !subjectName) return;

    dispatch(
      postAdaptiveSubTopicListRequest({
        topic_id: topicId,
        subject: subjectName,
      })
    );
  }, [dispatch, isCustomizeEnabled, topicId, subjectName]);

  useEffect(() => {
    if (!topicId || !subjectName) return;
  
    dispatch(
      fetchAdaptiveHomepageRequest({
        topic_id: topicId,
        subject: subjectName,
      })
    );
  }, [dispatch, topicId, subjectName]);
  useEffect(() => {
    if (!openUpgradeModalFromRoute) return;
    setIsUpgradeModalOpen(true);
  }, [openUpgradeModalFromRoute]);

  const cards = useMemo(
    () => getAdaptiveCards(adaptiveHomepage),
    [adaptiveHomepage]
  );
  const homepageRoot = useMemo(
    () => getHomepageRoot(adaptiveHomepage),
    [adaptiveHomepage]
  );
  const adaptiveQuestionIdForTopic = useMemo(
    () =>
      Number(
        pickValue(
          homepageRoot?.adaptive_question_id,
          homepageRoot?.adaptiveQuestionId,
          cards?.[0]?.adaptive_question_id,
          cards?.[0]?.adaptiveQuestionId,
          cards?.[0]?.id
        )
      ) || null,
    [homepageRoot, cards]
  );
  useEffect(() => {
    if (!adaptiveQuestionIdForTopic) return;
    dispatch(
      fetchAdaptiveAllQuestionListRequest({
        paper_type: 20,
        adaptive_question_id: adaptiveQuestionIdForTopic,
      })
    );
  }, [dispatch, adaptiveQuestionIdForTopic]);
  const isFreeUser = homepageRoot?.is_user_free === true || Number(homepageRoot?.is_user_free) === 1;
  useEffect(() => {
    if (isFreeUser) {
      setIsCustomizeEnabled(false);
    }
  }, [isFreeUser]);
  const freeDailyConsumptionKey = useMemo(() => {
    if (!adaptiveQuestionIdForTopic) return "";
    return `adaptive_qbank_free_daily_consumed_${adaptiveQuestionIdForTopic}_${getLocalDateKey()}`;
  }, [adaptiveQuestionIdForTopic]);
  const [hasConsumedFreeDailySet, setHasConsumedFreeDailySet] = useState(false);
  useEffect(() => {
    if (!freeDailyConsumptionKey) {
      setHasConsumedFreeDailySet(false);
      return;
    }
    setHasConsumedFreeDailySet(localStorage.getItem(freeDailyConsumptionKey) === "1");
  }, [freeDailyConsumptionKey]);
  useEffect(() => {
    if (!isFreeUser || !freeDailyConsumptionKey) return;
    if (completedFocusSetNoFromRoute <= 0) return;
    localStorage.setItem(freeDailyConsumptionKey, "1");
    setHasConsumedFreeDailySet(true);
  }, [completedFocusSetNoFromRoute, freeDailyConsumptionKey, isFreeUser]);
  // Always allow one free daily attempt.
  // Show lock popup only after user has completed that free attempt today.
  const isFreeUserLocked =
    isFreeUser && (hasConsumedFreeDailySet || openUpgradeModalFromRoute);
  const freeFocusSetInfo =
    homepageRoot?.focus_set_label_free || homepageRoot?.focusSetLabelFree || {};
  const focusSets = useMemo(() => {
    const perCycle = Number(
      pickValue(
        homepageRoot?.focus_sets_per_cycle,
        homepageRoot?.focusSetPerCycle,
        homepageRoot?.focus_set_per_cycle,
        homepageRoot?.learning_cycle_size,
        5
      )
    );
    const cycles = Number(
      pickValue(
        homepageRoot?.total_learning_cycles,
        homepageRoot?.total_cycles,
        homepageRoot?.learningCycles,
        2
      )
    );
    const progressMeta = getFallbackProgressMeta(homepageRoot);
    const fallbackSets = buildFallbackFocusSets(
      perCycle,
      cycles,
      progressMeta.completedByCycle,
      progressMeta.currentFocusSet
    );
    const parsed = getFocusSets(adaptiveHomepage);
    if (parsed.length === 0) return fallbackSets;

    // Always preserve full cycle layout (e.g. FS-1..FS-10) and layer API state on top.
    const parsedByOrder = new Map(parsed.map((set) => [set.order, set]));
    const merged = fallbackSets.map((baseSet) => {
      const apiSet = parsedByOrder.get(baseSet.order);
      if (!apiSet) return baseSet;
      return {
        ...baseSet,
        ...apiSet,
        // Keep fallback label/order/cycle if API misses them.
        label: apiSet.label || baseSet.label,
        order: Number.isFinite(apiSet.order) ? apiSet.order : baseSet.order,
        cycle: Number.isFinite(apiSet.cycle) ? apiSet.cycle : baseSet.cycle,
      };
    });

    const hasActive = merged.some((set) => set.active);
    if (hasActive) return merged;

    const activeOrder = progressMeta.currentFocusSet;
    if (Number.isFinite(activeOrder) && activeOrder > 0) {
      return merged.map((set) => ({
        ...set,
        active: set.order === activeOrder,
        completed: set.order < activeOrder ? true : set.completed,
      }));
    }

    return merged;
  }, [adaptiveHomepage, homepageRoot]);
  const displayFocusSets = useMemo(() => {
    const baseSets = focusSets;

    // Optimistic UI sync right after submit:
    // mark submitted set done and highlight next set as current.
    if (completedFocusSetNoFromRoute > 0 || nextFocusSetNoFromRoute > 0) {
      return baseSets.map((set) => ({
        ...set,
        completed:
          set.completed ||
          (completedFocusSetNoFromRoute > 0 && set.order === completedFocusSetNoFromRoute),
        active: nextFocusSetNoFromRoute > 0 ? set.order === nextFocusSetNoFromRoute : set.active,
      }));
    }

    return baseSets;
  }, [
    focusSets,
    completedFocusSetNoFromRoute,
    nextFocusSetNoFromRoute,
  ]);

  const pausedFocusSetNoFromStorage = useMemo(() => {
    if (!adaptiveQuestionIdForTopic) return 0;
    const storedValue = Number(
      localStorage.getItem(
        `adaptive_qbank_paused_focus_set_${adaptiveQuestionIdForTopic}`
      ) || 0
    );
    return Number.isFinite(storedValue) && storedValue > 0 ? storedValue : 0;
  }, [adaptiveQuestionIdForTopic]);
  const hasResumeFromStorage = pausedFocusSetNoFromStorage > 0;
  const hasResumeState = hasResumeFromStorage;
  const isPausedJourney =
    isPausedJourneyFromRoute || (!suppressResumeFromRoute && hasResumeState);
  const normalizedDisplayFocusSets = useMemo(() => {
    if (displayFocusSets.length === 0) return displayFocusSets;
    if (isPausedJourney) return displayFocusSets;

    const hasExplicitActive = displayFocusSets.some((set) => set.active);
    if (hasExplicitActive) return displayFocusSets;

    const firstIncompleteIndex = displayFocusSets.findIndex((set) => !set.completed);
    const activeIndex =
      firstIncompleteIndex >= 0 ? firstIncompleteIndex : displayFocusSets.length - 1;

    return displayFocusSets.map((set, index) => ({
      ...set,
      active: index === activeIndex,
    }));
  }, [displayFocusSets, isPausedJourney]);
  const cycleOne = normalizedDisplayFocusSets.slice(0, FOCUS_SETS_PER_LEARNING_CYCLE);
  const cycleTwo = normalizedDisplayFocusSets.slice(
    FOCUS_SETS_PER_LEARNING_CYCLE,
    FOCUS_SETS_PER_LEARNING_CYCLE * 2
  );
  const currentFocusSetNo = useMemo(() => {
    const activeSet = normalizedDisplayFocusSets.find((set) => set.active);
    return activeSet?.order || normalizedDisplayFocusSets?.[0]?.order || 1;
  }, [normalizedDisplayFocusSets]);
  const pausedFocusSetNo =
    pausedFocusSetNoFromRoute > 0
      ? pausedFocusSetNoFromRoute
      : pausedFocusSetNoFromStorage > 0
        ? pausedFocusSetNoFromStorage
      : isPausedJourney
        ? currentFocusSetNo
        : 0;
  const completedCycleOne = cycleOne.filter((set) => set.completed).length;
  const completedCycleTwo = cycleTwo.filter((set) => set.completed).length;

  const globalRequiredPerCycle = Number(
    pickValue(
      homepageRoot?.focus_sets_per_cycle,
      homepageRoot?.focusSetPerCycle,
      homepageRoot?.focus_set_per_cycle,
      homepageRoot?.learning_cycle_size
    )
  );
  const cycleOneRequired = Number(
    pickValue(
      homepageRoot?.cycle_1_focus_sets_required,
      homepageRoot?.cycle1_focus_sets_required,
      homepageRoot?.cycleOneFocusSetsRequired,
      Number.isFinite(globalRequiredPerCycle) && globalRequiredPerCycle > 0
        ? globalRequiredPerCycle
        : cycleOne.length
    )
  );
  const cycleTwoRequired = Number(
    pickValue(
      homepageRoot?.cycle_2_focus_sets_required,
      homepageRoot?.cycle2_focus_sets_required,
      homepageRoot?.cycleTwoFocusSetsRequired,
      Number.isFinite(globalRequiredPerCycle) && globalRequiredPerCycle > 0
        ? globalRequiredPerCycle
        : cycleTwo.length
    )
  );

  const canOpenCycleOneInsights =
    cycleOneRequired > 0 && completedCycleOne >= cycleOneRequired;
  const canOpenCycleTwoInsights =
    cycleTwoRequired > 0 && completedCycleTwo >= cycleTwoRequired;
  const cycleGroups = useMemo(() => {
    const sortedSets = [...normalizedDisplayFocusSets].sort((a, b) => a.order - b.order);
    const groups = [];
    for (let index = 0; index < sortedSets.length; index += FOCUS_SETS_PER_LEARNING_CYCLE) {
      const cycleNo = Math.floor(index / FOCUS_SETS_PER_LEARNING_CYCLE) + 1;
      groups.push({
        cycleNo,
        title: `Learning Cycle - ${toRoman(cycleNo)}`,
        sets: sortedSets.slice(index, index + FOCUS_SETS_PER_LEARNING_CYCLE),
      });
    }
    return groups;
  }, [normalizedDisplayFocusSets]);
  const canOpenCycleInsightsMap = useMemo(() => {
    const map = new Map();
    cycleGroups.forEach(({ cycleNo, sets }) => {
      if (cycleNo === 1) {
        map.set(cycleNo, canOpenCycleOneInsights);
        return;
      }
      if (cycleNo === 2) {
        map.set(cycleNo, canOpenCycleTwoInsights);
        return;
      }
      map.set(cycleNo, sets.length > 0 && sets.every((set) => set.completed));
    });
    return map;
  }, [cycleGroups, canOpenCycleOneInsights, canOpenCycleTwoInsights]);
  const defaultStartTopics = useMemo(() => {
    const customizeTopics =
      toArray(
        homepageRoot?.customize_topics?.topics ||
          homepageRoot?.customizeTopics?.topics ||
          homepageRoot?.customize_topics?.subtopics ||
          homepageRoot?.customizeTopics?.subtopics
      ) || [];

    return customizeTopics.slice(0, 2);
  }, [homepageRoot]);
  const structureInfo = useMemo(() => {
    const section =
      homepageRoot?.structure_info ||
      homepageRoot?.structureInfo ||
      {};

    const title = section?.title || "How this practice is structured";
    const points = toArray(section?.points);

    return {
      title,
      points: points.length
        ? points
        : [
            "5 Focus Sets form 1 Learning Cycle",
            "Each Focus Set has 10 questions from 2 subtopics",
            "Difficulty adapts as you progress",
          ],
    };
  }, [homepageRoot]);
  const showFreeFocusSetBanner = isFreeUserLocked;
  const visibleStartTopics = isCustomizeEnabled ? subtopics : defaultStartTopics;
  const shouldScrollStartTopics = visibleStartTopics.length > 2;
  const upgradePackageId = useMemo(
    () =>
      Number(
        pickValue(
          homepageRoot?.package_details?.package_id,
          homepageRoot?.package_details?.id,
          homepageRoot?.package_detail?.package_id,
          homepageRoot?.package_detail?.id,
          homepageRoot?.package_id,
          homepageRoot?.packageId,
          cards?.[0]?.package_details?.package_id,
          cards?.[0]?.package_details?.id,
          cards?.[0]?.package_id,
          cards?.[0]?.packageId
        )
      ) || null,
    [homepageRoot, cards]
  );
  const handleUpgradeCtaClick = () => {
    setIsUpgradeModalOpen(false);
    if (upgradePackageId) {
      history.push(`/package/${upgradePackageId}`);
      return;
    }
    history.push("/packages");
  };

  const handleOpenCustomizeModal = (subtopic) => {
    if (isFreeUser) {
      setIsUpgradeModalOpen(true);
      return;
    }
    const selectedSubtopicId =
      subtopic?.sub_topic_id || subtopic?.subtopic_id || subtopic?.id || "";
    const selectedDifficulty = String(
      subtopic?.current_difficulty ||
        subtopic?.selecteddifficulty ||
        subtopic?.difficulty ||
        subtopic?.mastery_tag ||
        subtopic?.level ||
        "EASY"
    ).toUpperCase();

    if (topicId && subjectName && selectedSubtopicId) {
      dispatch(
        postAdaptiveSubTopicListRequest({
          subject: subjectName,
          topic_id: topicId,
          selected_subtopic_id: selectedSubtopicId,
          selecteddifficulty: selectedDifficulty,
        })
      );
    }

    setSelectedCustomizeTopic(subtopic);
    setIsCustomizeModalOpen(true);
  };

  const handleCloseCustomizeModal = () => {
    setIsCustomizeModalOpen(false);
    setSelectedCustomizeTopic(null);
  };

  const handleSaveCustomizeModal = ({ topicId: selectedSubtopicId, difficulty: selectedDifficulty }) => {
    if (!topicId || !subjectName || !selectedSubtopicId || !selectedDifficulty) {
      handleCloseCustomizeModal();
      return;
    }

    dispatch(
      postAdaptiveSubTopicListRequest({
        subject: subjectName,
        topic_id: topicId,
        selected_subtopic_id: selectedSubtopicId,
        selecteddifficulty: String(selectedDifficulty).toUpperCase(),
      })
    );

    handleCloseCustomizeModal();
  };
  const handleFocusSetClick = (item) => {
    const isPausedSet = pausedFocusSetNo === item.order && !item.completed;
    const canOpenCompletedSummary = showFreeFocusSetBanner && item.completed;
    if (!item.active && !isPausedSet && !canOpenCompletedSummary) return;

    if (canOpenCompletedSummary) {
      if (!adaptiveQuestionIdForTopic) return;
      history.push({
        pathname: "/practice/adaptive-question-bank/submit",
        state: {
          adaptive_question_id: adaptiveQuestionIdForTopic,
          paper_type: 20,
          topic,
          topic_id: topicId,
          focusSetNo: item.order,
        },
      });
      return;
    }

    const isFreeFirstSet = isFreeUser && item.order === 1 && !isFreeUserLocked;
    if (showFreeFocusSetBanner) {
      setIsUpgradeModalOpen(true);
      return;
    }
    history.push({
      pathname: "/test",
      state: {
        useAdaptiveTestScreen: true,
        isResume: isPausedJourney && pausedFocusSetNo === item.order,
        adaptive_question_id: adaptiveQuestionIdForTopic || undefined,
        paper_type: 20,
        topic,
        topic_id: topicId,
        focusSetNo: item.order,
        // Keep existing free-user flow while still opening FS-1 questions.
        noSetPresent: !isFreeFirstSet,
      },
    });
  };

  const handleOpenLearningInsights = (cycleNo) => {
    if (!topicId || !subjectName || !cycleNo) return;
    dispatch(
      postAdaptiveLearningSummaryRequest({
        subject: subjectName,
        topic_id: topicId,
        learning_cycle: cycleNo,
      })
    );
    history.push({
      pathname: "/practice/adaptive-question-bank/learning-summary",
      state: {
        topic,
        topic_id: topicId,
        subject: subjectName,
        learning_cycle: cycleNo,
        focusSetNo: currentFocusSetNo,
      },
    });
  };
  const handleCustomizeToggle = () => {
    if (isFreeUser) {
      setIsUpgradeModalOpen(true);
      return;
    }
    setIsCustomizeEnabled((prev) => !prev);
  };

  const renderCycle = ({
    title,
    sets,
    canOpenInsights,
    showCompletedBadge,
    cycleNo,
  }) => (
    <div className="aqb-next-cycle-col" key={`cycle-${cycleNo}`}>
      <div className="aqb-next-cycle-head">
        <h5>{title}</h5>
        {showCompletedBadge ? (
          <span className="aqb-cycle-completed">Completed</span>
        ) : null}
      </div>
      <div className="aqb-next-dots">
        {sets.map((item) => (
          (() => {
            const isPausedSet = pausedFocusSetNo === item.order && !item.completed;
            const isFocusSetClickable =
              (showFreeFocusSetBanner && item.completed) ||
              (!showFreeFocusSetBanner && (item.active || isPausedSet));
            return (
          <button
            type="button"
            key={item.label}
            className={`aqb-dot ${item.completed ? "completed" : ""} ${
              item.active ? "active" : ""
            } ${isPausedSet ? "paused" : ""}`}
            disabled={!isFocusSetClickable}
            onClick={() => handleFocusSetClick(item)}
            aria-label={
              isFocusSetClickable
                ? showFreeFocusSetBanner && item.completed
                  ? `View ${item.label} summary`
                  : `Open ${item.label}`
                : `${item.label} (locked)`
            }
          >
            {item.completed ? (
              <img
                className="aqb-dot-completed-image"
                src={images.completed_focus_set}
                alt=""
              />
            ) : isPausedSet ? (
              <img
                className="aqb-dot-completed-image"
                src={images.pause_icon}
                alt=""
              />
            ) : null}
            <span className="aqb-dot-number">{item.order}</span>
            <span className="aqb-dot-label">{item.label}</span>
          </button>
            );
          })()
        ))}
      </div>
      <button
        type="button"
        className={`aqb-insights-btn ${canOpenInsights ? "" : "disabled"}`}
        disabled={!canOpenInsights}
        onClick={() => handleOpenLearningInsights(cycleNo)}
      >
        View learning insights
      </button>
    </div>
  );

  return (
    <div className="aqb-next-page">
      <div className="aqb-next-header">
        <img
          className="aqb-next-back"
          src={images.new_backarrow}
          alt="Back"
          onClick={() => history.push("/practice/adaptive-question-bank")}
        />
        <h2>Adaptive Question Bank</h2>
      </div>

      <div className="aqb-next-body">
        <section className="aqb-next-main">
          <div className="aqb-next-top-card">
            <div>
            <h3>{topicName || "Topic"}</h3>
            <p className="aqb-topic-desc">
              Focused practice built from unlimited questions
            </p>
            </div>
            <div className="aqb-next-stats">
              <div className="aqb-stat-card">
                <span className="aqb-stat-icon aqb-stat-icon-purple">
                  <img
                    className="aqb-stat-icon-image"
                    src={images.question_per_focus_set}
                    alt=""
                  />
                </span>
                <span>10 Questions</span>
                <small>Per Focus Set</small>
              </div>
              <div className="aqb-stat-card">
                <span className="aqb-stat-icon aqb-stat-icon-blue">
                  <img
                    className="aqb-stat-icon-image"
                    src={images.unlimited_focus_sets}
                    alt=""
                  />
                </span>
                <span>Unlimited</span>
                <small>Focus Sets</small>
              </div>
              <div className="aqb-stat-card">
                <span className="aqb-stat-icon aqb-stat-icon-gold">
                  <img
                    className="aqb-stat-icon-image"
                    src={images.difficulty_levels}
                    alt=""
                  />
                </span>
                <span>3 Difficulty</span>
                <small>Levels</small>
              </div>
            </div>
          </div>
          {showFreeFocusSetBanner ? (
            <div className="aqb-free-focus-banner">
              <div className="aqb-free-focus-banner__content">
                <p className="aqb-free-focus-banner__title">
                  {freeFocusSetInfo?.message || "Today's free Focus Set is complete."}
                </p>
                <p className="aqb-free-focus-banner__subtitle">
                  {freeFocusSetInfo?.unlock_msg || "Unlock unlimited practice."}
                </p>
              </div>
              <button
                type="button"
                className="aqb-free-focus-banner__button"
                onClick={() => setIsUpgradeModalOpen(true)}
                aria-label="Unlock unlimited practice"
              >
                {freeFocusSetInfo?.button_text || "Unlock"}
              </button>
            </div>
          ) : null}

          <div className="aqb-next-content-row">
            <div className="aqb-next-progress-wrap">
              <div className="aqb-next-progress-head">
                <h4>Your Progress</h4>
                <button
                  type="button"
                  onClick={() =>
                    history.push({
                      pathname: "/practice/adaptive-question-bank/subtopic-progress",
                      state: { topic },
                    })
                  }
                >
                  Topic Mastery List
                </button>
              </div>

              <div
                className="aqb-next-cycle"
                style={{
                  "--aqb-cycle-count": String(Math.max(cycleGroups.length, 1)),
                }}
              >
                {cycleGroups.map((group) => {
                  const canOpenInsights = Boolean(
                    canOpenCycleInsightsMap.get(group.cycleNo)
                  );
                  return renderCycle({
                    title: group.title,
                    sets: group.sets,
                    canOpenInsights,
                    showCompletedBadge: canOpenInsights,
                    cycleNo: group.cycleNo,
                  });
                })}
              </div>

              <div className="aqb-next-structure">
                <h6>
                  <img src={structureIcon} alt="" aria-hidden="true" className="aqb-next-structure-icon" />
                  <span>{structureInfo.title}</span>
                </h6>
                {structureInfo.points.map((point, index) => (
                  <p key={`${point}-${index}`}>- {point}</p>
                ))}
              </div>
            </div>

            <aside className="aqb-next-side-card">
              <h4>Start Focus Set</h4>
              {isFreeUser ? (
                <div
                  className="aqb-next-toggle aqb-next-toggle--free-locked"
                  role="button"
                  tabIndex={0}
                  onClick={handleCustomizeToggle}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCustomizeToggle();
                    }
                  }}
                  aria-label="Customize Topics — upgrade to unlock"
                >
                  <span>Customize Topics</span>
                  <span
                    className="aqb-switch-toggle aqb-switch-toggle--decorative"
                    aria-hidden
                  >
                    <span className="aqb-switch disabled" />
                  </span>
                </div>
              ) : (
                <div className="aqb-next-toggle">
                  <span>Customize Topics</span>
                  <button
                    type="button"
                    className="aqb-switch-toggle"
                    onClick={handleCustomizeToggle}
                    aria-pressed={isCustomizeEnabled}
                    aria-label="Toggle customize topics"
                  >
                    <span className={`aqb-switch ${isCustomizeEnabled ? "enabled" : "disabled"}`} />
                  </button>
                </div>
              )}

              <div className={`aqb-subtopic-list ${shouldScrollStartTopics ? "scrollable" : ""}`}>
                {isCustomizeEnabled ? (
                  loading?.subTopicList ? (
                    <p className="aqb-customize-hint">Loading subtopics...</p>
                  ) : subtopics.length > 0 ? (
                    subtopics.map((sub) => (
                      <div
                        className="aqb-topic-item"
                        key={
                          sub.subtopic_id ||
                          sub["sub_topic_id"] ||
                          sub.id ||
                          sub.subtopic_name ||
                          sub["sub topic name"]
                        }
                      >
                        <div className="aqb-topic-item-content">
                          <p>
                            {sub.subtopic_name ||
                              sub["sub topic name"] ||
                              sub.sub_topic_name ||
                              sub.name ||
                              "Untitled Subtopic"}
                          </p>
                          <div className="aqb-topic-tags">
                            <span>{sub.current_difficulty || sub.level || "Easy"}</span>
                            <span>
                              {sub.ui_status ||
                                sub.subtopic_status ||
                                sub.status ||
                                "Not Started"}
                            </span>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="aqb-topic-edit"
                          onClick={() => handleOpenCustomizeModal(sub)}
                          aria-label="Edit subtopic"
                        >
                          <img src={editIcon} alt="" aria-hidden="true" />
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="aqb-customize-hint">No subtopics found</p>
                  )
                ) : defaultStartTopics.length > 0 ? (
                  defaultStartTopics.map((sub) => (
                    <div
                      className="aqb-topic-item"
                      key={
                        sub.subtopic_id ||
                        sub["sub_topic_id"] ||
                        sub.id ||
                        sub.subtopic_name ||
                        sub.sub_topic_name
                      }
                    >
                      <div className="aqb-topic-item-content">
                        <p>
                          {sub.subtopic_name ||
                            sub["sub topic name"] ||
                            sub.sub_topic_name ||
                            sub.name ||
                            "Untitled Subtopic"}
                        </p>
                        <div className="aqb-topic-tags">
                          <span>{sub.current_difficulty || sub.level || "Easy"}</span>
                          <span>
                            {sub.ui_status ||
                              sub.subtopic_status ||
                              sub.status ||
                              "Not Started"}
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="aqb-topic-edit"
                        disabled
                        aria-disabled="true"
                        aria-label="Enable Customize Topics to edit"
                        style={{ opacity: 0.55, cursor: "not-allowed" }}
                      >
                        <img
                          src={editIcon}
                          alt=""
                          aria-hidden="true"
                          style={{ filter: "grayscale(100%) brightness(0.72)" }}
                        />
                      </button>
                    </div>
                  ))
                ) : (
                  <p className="aqb-customize-hint">
                    Enable Customize Topics to view subtopics for this topic.
                  </p>
                )}
              </div>

              <button
                type="button"
                className={`aqb-next-start-btn ${isPausedJourney ? "resume" : ""}`}
                onClick={() => {
                  if (showFreeFocusSetBanner) {
                    setIsUpgradeModalOpen(true);
                    return;
                  }
                  history.push({
                    pathname: "/test",
                    state: {
                      useAdaptiveTestScreen: true,
                      isResume: isPausedJourney,
                      adaptive_question_id: adaptiveQuestionIdForTopic || undefined,
                      paper_type: 20,
                      topic,
                      topic_id: topicId,
                      focusSetNo:
                        pausedFocusSetNo > 0
                          ? pausedFocusSetNo
                          : currentFocusSetNo,
                    },
                  });
                }}
              >
                {showFreeFocusSetBanner
                  ? freeFocusSetInfo?.button_text || "Unlock"
                  : isPausedJourney
                    ? "Resume"
                    : "Start Next Focus Set"}
              </button>

            </aside>
          </div>
        </section>
      </div>
      <CustomizeSubtopicModal
        isOpen={isCustomizeModalOpen}
        onClose={handleCloseCustomizeModal}
        onSave={handleSaveCustomizeModal}
        topics={subtopics}
        initialTopicId={
          selectedCustomizeTopic?.subtopic_id ||
          selectedCustomizeTopic?.sub_topic_id ||
          selectedCustomizeTopic?.id ||
          ""
        }
        initialDifficulty={selectedDifficultyLevel || getDifficultyValue(selectedCustomizeTopic)}
      />
      <AdaptiveUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onCtaClick={handleUpgradeCtaClick}
      />
    </div>
  );
};

export default AdaptiveNextFocusSet;
